import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      type: 'info', // default type
      duration: 5000, // default duration
      ...toast,
    };

    setToasts(prev => [...prev, newToast]);

    // Auto remove toast after duration
    if (newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Convenience methods for different toast types
  const showSuccess = useCallback((message, options = {}) => {
    return addToast({
      type: 'success',
      title: 'Success',
      message,
      ...options
    });
  }, [addToast]);

  const showError = useCallback((message, options = {}) => {
    return addToast({
      type: 'error',
      title: 'Error',
      message,
      duration: 7000, // Errors stay longer
      ...options
    });
  }, [addToast]);

  const showWarning = useCallback((message, options = {}) => {
    return addToast({
      type: 'warning',
      title: 'Warning',
      message,
      ...options
    });
  }, [addToast]);

  const showInfo = useCallback((message, options = {}) => {
    return addToast({
      type: 'info',
      title: 'Info',
      message,
      ...options
    });
  }, [addToast]);

  const showLoading = useCallback((message, options = {}) => {
    return addToast({
      type: 'loading',
      title: 'Loading',
      message,
      duration: 0, // Loading toasts don't auto-dismiss
      ...options
    });
  }, [addToast]);

  // Convenience method for API responses
  const showApiResponse = useCallback((response, successMessage, errorMessage) => {
    if (response.success) {
      showSuccess(successMessage || response.message || 'Operation completed successfully');
    } else {
      showError(errorMessage || response.error?.message || 'An error occurred');
    }
  }, [showSuccess, showError]);

  // Method for transaction notifications
  const showTransaction = useCallback((type, tokenSymbol, amount, txHash) => {
    const messages = {
      buy: `Successfully bought ${amount} ${tokenSymbol}`,
      sell: `Successfully sold ${amount} ${tokenSymbol}`,
      transfer: `Successfully transferred ${amount} ${tokenSymbol}`,
      approve: `Successfully approved ${tokenSymbol}`,
    };

    return showSuccess(messages[type] || 'Transaction completed', {
      action: txHash ? {
        label: 'View Transaction',
        onClick: () => window.open(`https://solscan.io/tx/${txHash}`, '_blank')
      } : null
    });
  }, [showSuccess]);

  // Method for confirmation toasts (replacement for window.confirm)
  const showConfirmation = useCallback((message, onConfirm, onCancel) => {
    return addToast({
      type: 'warning',
      title: 'Confirmation Required',
      message,
      duration: 0, // Don't auto-dismiss
      action: {
        label: 'Confirm',
        onClick: () => {
          if (onConfirm) onConfirm();
        }
      },
      cancelAction: {
        label: 'Cancel',
        onClick: () => {
          if (onCancel) onCancel();
        }
      }
    });
  }, [addToast]);

  // Generic showToast method for backward compatibility
  const showToast = useCallback((message, type = 'info', options = {}) => {
    const toastMethods = {
      success: showSuccess,
      error: showError,
      warning: showWarning,
      info: showInfo,
      loading: showLoading
    };
    
    const method = toastMethods[type] || showInfo;
    return method(message, options);
  }, [showSuccess, showError, showWarning, showInfo, showLoading]);

  const value = useMemo(() => ({
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    showApiResponse,
    showTransaction,
    showConfirmation,
    showToast
  }), [toasts, addToast, removeToast, clearAllToasts, showSuccess, showError, showWarning, showInfo, showLoading, showApiResponse, showTransaction, showConfirmation, showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};